import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Activity, BookOpen, ClipboardList, Gamepad2, LayoutDashboard, LineChart, Map, Settings, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Logo } from "../../components/brand/Logo";
import { Shimmer, ThemeToggle } from "../../components/brand/Experience";
import { useI18n } from "../../i18n";

const NAV = [
  ["overview", "/care", LayoutDashboard], ["patients", "/care/patients", Users], ["assessments", "/care/assessments", ClipboardList],
  ["sessions", "/care/sessions", Activity], ["progress", "/care/progress", LineChart], ["missions", "/care/missions", Map], ["exercises", "/care/exercises", BookOpen],
  ["settings", "/care/settings", Settings],
];

export default function CareLayout() {
  const { t } = useI18n();
  const nav = useNavigate();
  const { data: ther } = useQuery({ queryKey: ["therapist"], queryFn: () => api.get("/therapist").then((r) => r.data) });
  const link = ({ isActive }) =>
    `flex h-11 shrink-0 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition-colors ${isActive ? "bg-brand text-white" : "text-white/75 hover:bg-white/5 hover:text-white"}`;
  return (
    <div className="min-h-screen bg-background lg:flex">
      <a href="#main-content" data-testid="skip-to-content" className="sr-only z-50 rounded-xl bg-[#E0B33A] px-4 py-3 font-bold text-[#0A2540] focus:not-sr-only focus:fixed focus:left-4 focus:top-4">Aller au contenu</a>
      <aside className="no-print bg-navy-depth text-white lg:sticky lg:top-0 lg:h-screen lg:w-64 lg:shrink-0">
        <div className="flex items-center justify-between gap-3 p-4 pt-[max(1rem,env(safe-area-inset-top))] lg:p-6">
          <button onClick={() => nav("/")} data-testid="care-home-link" className="flex min-h-11 items-center gap-3">
            <Logo size={38} />
            <div className="text-left leading-none"><div className="font-display text-xl font-semibold">Baobab Care</div><div className="mt-1 text-[9px] font-bold tracking-[0.28em] text-[#4FD1C5]">BOMBACARE CONNECT</div></div>
          </button>
          <div className="flex items-center gap-2"><ThemeToggle />
          <button data-testid="switch-to-patient-mode" onClick={() => nav("/patient")} className="flex h-11 items-center gap-2 rounded-full bg-white/10 px-4 text-xs font-bold active:bg-white/20 lg:hidden">
            <Gamepad2 className="h-4 w-4" />Patient
          </button></div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:px-4">
          {NAV.map(([k, to, I]) => (
            <NavLink key={k} to={to} end={to === "/care"} className={link} data-testid={`therapist-nav-${k}`}><I className="h-4 w-4" />{t(`nav_${k}`)}</NavLink>
          ))}
        </nav>
        <div className="hidden p-4 lg:absolute lg:bottom-0 lg:block lg:w-64">
          <button data-testid="switch-to-patient-mode-desktop" onClick={() => nav("/patient")} className="mb-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-white/15 text-sm font-bold hover:bg-white/5">
            <Gamepad2 className="h-4 w-4" />Passer en mode patient
          </button>
          <p className="text-sm font-semibold">{ther?.name}</p><p className="text-xs text-white/60">{ther?.profession}</p>
        </div>
      </aside>
      <main id="main-content" tabIndex={-1} className="baobab-watermark min-w-0 flex-1 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] outline-none sm:p-8 lg:p-10"><Outlet /></main>
    </div>
  );
}

export const PageHeader = ({ title, sub, children }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div><h1 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">{title}</h1>{sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}</div>
    <div className="no-print flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Panel = ({ title, children, className = "", testId, right }) => (
  <section data-testid={testId} className={`shadow-soft min-w-0 rounded-3xl border border-border/70 bg-card p-5 sm:p-6 ${className}`}>
    {title && <div className="mb-4 flex items-center justify-between"><h3 style={{ fontFamily: "Manrope, sans-serif" }} className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">{title}</h3>{right}</div>}
    {children}
  </section>
);

export const Kpi = ({ label, value, unit, testId, hint, loading }) => (
  <div data-testid={testId} className="shadow-soft lift relative overflow-hidden rounded-3xl border border-border/70 bg-card p-5">
    <span aria-hidden className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#17A2A2] via-[#17A2A2]/40 to-[#E0B33A]/60" />
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    {loading ? <Shimmer className="mt-3 h-9 w-24" /> : <p className="mt-2 font-display text-4xl font-semibold text-foreground">{value ?? "—"}<span className="ml-1 text-base font-semibold text-muted-foreground/80">{unit}</span></p>}
    {hint && <p className="mt-1 text-xs text-muted-foreground/80">{hint}</p>}
  </div>
);

export const SimBadge = () => (
  <span data-testid="simulated-badge" className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold tracking-widest text-amber-800">DONNÉES DE MOUVEMENT SIMULÉES</span>
);

export const Delta = ({ v, inverse = false, unit = "" }) => {
  if (v === null || v === undefined) return <span className="text-muted-foreground/80">—</span>;
  const good = inverse ? v < 0 : v > 0;
  return <span className={`font-mono text-xs font-bold ${v === 0 ? "text-muted-foreground" : good ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{v > 0 ? "+" : ""}{v}{unit}</span>;
};
